/**
 * Keep in sync with frontend/api/formatEventDate.mjs (OG function + node tests).
 *
 * Format an event date for display. Never returns "Invalid Date".
 *
 * Events store calendar dates as "YYYY-MM-DD" (no timezone). Empty, null,
 * and unparseable values return `fallback` (default ""). ISO datetimes are
 * reduced to their calendar date so we don't shift a day in IST vs UTC.
 */
export function formatEventDate(dateStr, options = {}) {
  const fallback = options.fallback ?? "";
  const weekday = options.weekday ?? "short";

  if (dateStr == null) return fallback;

  let year;
  let month;
  let day;

  if (dateStr instanceof Date) {
    if (Number.isNaN(dateStr.getTime())) return fallback;
    year = dateStr.getUTCFullYear();
    month = dateStr.getUTCMonth() + 1;
    day = dateStr.getUTCDate();
  } else {
    const raw = String(dateStr).trim();
    if (!raw) return fallback;

    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      year = Number(ymd[1]);
      month = Number(ymd[2]);
      day = Number(ymd[3]);
    } else {
      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return fallback;
      year = parsed.getUTCFullYear();
      month = parsed.getUTCMonth() + 1;
      day = parsed.getUTCDate();
    }
  }

  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return fallback;

  const utc = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(utc.getTime())) return fallback;
  if (utc.getUTCFullYear() !== year || utc.getUTCMonth() !== month - 1 || utc.getUTCDate() !== day) {
    return fallback;
  }

  return utc.toLocaleDateString("en-US", {
    weekday,
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Local midnight Date for a YYYY-MM-DD (or ISO) event date, or null. */
export function parseEventCalendarDate(dateStr) {
  if (dateStr == null) return null;
  const raw = String(dateStr).trim();
  if (!raw) return null;
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!ymd) return null;
  const year = Number(ymd[1]);
  const month = Number(ymd[2]);
  const day = Number(ymd[3]);
  const local = new Date(year, month - 1, day);
  if (Number.isNaN(local.getTime())) return null;
  if (local.getFullYear() !== year || local.getMonth() !== month - 1 || local.getDate() !== day) {
    return null;
  }
  return local;
}

/** Event heading/title: "Name — Wed, September 18, 2026" or just the name. */
export function eventDisplayTitle(event) {
  const name = (event && (event.event_name || event.venue_name)) || "Mahjong event";
  const when = formatEventDate(event && event.date);
  return when ? `${name} — ${when}` : name;
}
